import React from 'react';
import IconSearch from '../../../images/svg/search.svg';
import { borderRadius, breakpoints } from '../tokens';
import { colors } from '../colors';
import Input from '../input';

/*
* A logic-less search bar with an autocomplete dropdown
* */
interface SearchBarProps {
    value: string
    onChange: (value: string) => void
    suggestions?: { label: string, data: unknown }[]
    onSuggestionClick?: (data: unknown) => void
    bordered?: boolean
    white?: boolean
    nativeAutoComplete?: boolean
    placeholder: string,
    onFocus?: () => void
    disabled?: boolean
    autoFocus?: boolean
}

const SearchBar = ({
    value,
    onChange,
    suggestions,
    onSuggestionClick,
    bordered,
    white,
    placeholder,
    onFocus,
    disabled,
    autoFocus,
    nativeAutoComplete,
}: SearchBarProps): JSX.Element => {
    const suggestionsRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        let animationFrameId: number;

        const focusInput = (): void => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        };

        const handleAnimationFrame = (): void => {
            focusInput();
        };

        if (autoFocus && inputRef.current) {
            animationFrameId = requestAnimationFrame(handleAnimationFrame);
        }

        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [autoFocus]);

    return (
        <div css={{
            height: '48px',
            position: 'relative',
            width: '100%',
        }}
        >
            <div css={{
                width: '100%',
                display: 'flex',
                position: 'absolute',
                [breakpoints.tb]: {
                    flexDirection: 'column',
                    position: 'relative',
                },
            }}
            >
                <div css={{
                    position: 'relative',
                    zIndex: 10,
                    borderRadius: '24px',
                    width: '100%',
                    border: bordered ? `2px solid ${colors.primaryOrange}` : '',
                    backgroundColor: white ? colors.shadesWhite : colors.grey100,
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0), 0 4px 6px -2px rgba(0, 0, 0, 0)',
                    transition: '0.3s box-shadow',
                    '&:focus-within': {
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        '.suggestions': {
                            display: 'block',
                        },
                        input: {
                            borderBottomLeftRadius: (suggestions && suggestions.length > 0) ? '0px' : '24px',
                            borderBottomRightRadius: (suggestions && suggestions.length > 0) ? '0px' : '24px',
                        },
                    },
                    [breakpoints.tb]: {
                        position: 'absolute',
                    },
                }}
                >
                    <div css={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        width: '100%',
                    }}
                    >
                        <IconSearch css={{
                            position: 'absolute',
                            marginLeft: '16px',
                            flexShrink: 0,
                            stroke: bordered ? colors.primaryOrange : colors.shadesBlack,
                        }}
                        />
                        <div css={{
                            width: '100%',
                        }}
                        >
                            <Input
                                autoComplete={nativeAutoComplete ? 'on' : 'off'} // enables/disables to native autocomplete (not the suggestions)
                                ref={inputRef}
                                hideLabel
                                css={{
                                    border: 'none',
                                    maxWidth: '100%',
                                    padding: '8px',
                                    height: '44px',
                                    backgroundColor: 'transparent',
                                    paddingLeft: '42px',
                                    borderRadius: '24px',
                                    ':not(:placeholder-shown)': {
                                        border: 'none',
                                    },
                                    [breakpoints.tb]: {
                                        maxWidth: '100%',
                                    },
                                }}
                                disabled={disabled}
                                name="q"
                                type="text"
                                placeholder={placeholder}
                                value={value}
                                onChange={(e) => {
                                    if ('value' in e.target && typeof e.target.value === 'string') {
                                        onChange(e.target.value);
                                    }
                                }}
                                onFocus={onFocus}
                                onKeyDown={(e) => {
                                    if (e.key === 'ArrowDown' && suggestions && suggestions.length > 0) {
                                        e.preventDefault();
                                        suggestionsRef.current?.querySelector('button')?.focus();
                                    } else if (e.key === 'ArrowUp' && suggestions && suggestions.length > 0) {
                                        e.preventDefault();
                                        if (suggestionsRef.current) {
                                            const selector = suggestionsRef.current?.querySelector('button:last-child');
                                            if (selector && 'focus' in selector && typeof selector.focus === 'function') {
                                                selector.focus();
                                            }
                                        }
                                    }
                                }}
                            />
                        </div>
                        <div
                            css={{
                                width: '36px',
                                height: '100%',
                                position: 'absolute',
                                right: '0',
                                top: '11px',
                            }}
                            style={{
                                display: value ? 'unset' : 'none',
                            }}
                        >
                            <button
                                type="button"
                                aria-label="Clear Search"
                                aria-disabled={!value}
                                onClick={() => {
                                    onChange('');
                                    if (inputRef.current) {
                                        inputRef.current.focus();
                                    }
                                }}
                                css={{
                                    width: '22px',
                                    height: '22px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    position: 'relative',
                                    justifyContent: 'center',
                                    borderRadius: borderRadius.full,
                                    '&:hover': {
                                        backgroundColor: colors.grey200,
                                    },
                                }}
                            >
                                <div css={{
                                    position: 'relative',
                                    width: '14px',
                                    height: '14px',
                                    maxHeight: '14px',
                                    transform: 'translateY(6px)',
                                }}
                                >
                                    <div css={{
                                        transform: 'rotate(45deg)',
                                        position: 'absolute',
                                        width: '14px',
                                        height: '1px',
                                        backgroundColor: colors.grey400,
                                    }}
                                    />
                                    <div css={{
                                        transform: 'rotate(-45deg)',
                                        position: 'absolute',
                                        width: '14px',
                                        height: '1px',
                                        backgroundColor: colors.grey400,
                                    }}
                                    />
                                </div>
                            </button>
                        </div>
                    </div>
                    {suggestions && suggestions.length > 0 && (
                        <div
                            className="suggestions"
                            css={{
                                display: 'none',
                                maxWidth: '100%',
                            }}
                            ref={suggestionsRef}
                            tabIndex={0}
                            role="listbox"
                        >
                            {suggestions.map(item => (
                                <button
                                    key={item.label}
                                    type="button"
                                    css={{
                                        display: 'flex',
                                        paddingLeft: '16px',
                                        paddingRight: '16px',
                                        alignItems: 'center',
                                        width: '100%',
                                        height: '40px',
                                        borderStyle: 'none',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        transition: '0.2s',
                                        '&:hover, &:focus': {
                                            backgroundColor: colors.grey200,
                                            paddingLeft: '20px',
                                        },
                                        '&:last-of-type': {
                                            borderBottomLeftRadius: '24px',
                                            borderBottomRightRadius: '24px',
                                        },
                                    }}
                                    onClick={() => {
                                        onChange(item.label);
                                        if (onSuggestionClick) {
                                            onSuggestionClick(item.data);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            const currentSuggestionElement = e.currentTarget;
                                            const nextSuggestionElement = currentSuggestionElement
                                                .nextElementSibling as HTMLButtonElement | undefined;

                                            if (nextSuggestionElement) {
                                                nextSuggestionElement.focus();
                                            } else if (inputRef.current) {
                                                inputRef.current.focus();
                                            }
                                        }

                                        if (e.key === 'ArrowUp') {
                                            e.preventDefault();
                                            const currentSuggestionElement = e.currentTarget;
                                            const prevSuggestionElement = currentSuggestionElement
                                                .previousElementSibling as HTMLButtonElement | undefined;

                                            if (prevSuggestionElement) {
                                                prevSuggestionElement.focus();
                                            } else if (inputRef.current) {
                                                inputRef.current.focus();
                                            }
                                        }
                                    }}
                                >
                                    <span css={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    >
                                        {item.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchBar;
